import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0.00';
  const v = Number(n);
  const s = v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-$${s.slice(1)}` : `$${s}`;
};
const fmtInt = (n) => (n ?? 0).toLocaleString('en-US');
const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
};
const fmtDays = (n) => {
  if (n === null || n === undefined) return '—';
  if (n === 0) return 'same day';
  return `${fmtInt(n)} day${n === 1 ? '' : 's'}`;
};
const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  const s = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return `${n >= 0 ? '+' : '−'}${s}%`;
};

export default function AdminPanel({ onClose }) {
  const [tab, setTab] = useState('analytics');
  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel admin-panel-wide" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <div className="admin-tabs">
            <button
              className={`admin-tab ${tab === 'analytics' ? 'is-active' : ''}`}
              onClick={() => setTab('analytics')}
            >📊 Analytics</button>
            <button
              className={`admin-tab ${tab === 'insights' ? 'is-active' : ''}`}
              onClick={() => setTab('insights')}
            >💡 Insights</button>
            <button
              className={`admin-tab ${tab === 'audit' ? 'is-active' : ''}`}
              onClick={() => setTab('audit')}
            >🔤 Name Audit</button>
            <button
              className={`admin-tab ${tab === 'whitelist' ? 'is-active' : ''}`}
              onClick={() => setTab('whitelist')}
            >👥 Whitelist</button>
          </div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>
        {tab === 'analytics' && <AnalyticsTab />}
        {tab === 'insights' && <InsightsTab />}
        {tab === 'audit' && <NameAuditTab />}
        {tab === 'whitelist' && <WhitelistTab />}
      </div>
    </div>
  );
}

// ─── Analytics ───────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('lifetime'); // lifetime | year | range
  const [year, setYear] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [owner, setOwner] = useState('All');
  const [topMode, setTopMode] = useState('item'); // 'item' | 'order'
  const [showAddWithdrawal, setShowAddWithdrawal] = useState(false);

  const fetchData = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ period, owner });
      if (period === 'year' && year) params.set('year', year);
      if (period === 'range') {
        if (from) params.set('from', from);
        if (to) params.set('to', to);
      }
      if (opts.refresh) params.set('refresh', '1');
      const res = await authFetch(`/api/admin/analytics?${params.toString()}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
      // Default the year selector to most recent once available
      if (!year && json.available_years?.length) {
        setYear(String(json.available_years[json.available_years.length - 1]));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, period, year, from, to, owner]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ownerOptions = useMemo(() => {
    const owners = data?.owners?.length ? data.owners : ['Ben', 'Spencer', 'Spencer\\Ben'];
    return ['All', ...owners];
  }, [data]);

  const summary = data?.summary || {};
  const byOwner = data?.by_owner || [];
  const monthly = data?.monthly || [];
  const yearly  = data?.yearly  || [];
  const topSales = data?.top_sales || [];
  const topOrders = data?.top_orders || [];

  const monthlyMax = Math.max(1, ...monthly.map(m => m.revenue || 0));

  return (
    <div className="analytics">
      {error && <div className="admin-error">{error}</div>}

      <div className="analytics-controls">
        <div className="ctl-group">
          <label className="ctl-label">Period</label>
          <div className="seg">
            {[
              ['lifetime', 'Lifetime'],
              ['year', 'Yearly'],
              ['range', 'Custom'],
            ].map(([v, l]) => (
              <button
                key={v}
                className={`seg-btn ${period === v ? 'is-active' : ''}`}
                onClick={() => setPeriod(v)}
              >{l}</button>
            ))}
          </div>
        </div>

        {period === 'year' && (
          <div className="ctl-group">
            <label className="ctl-label">Year</label>
            <select className="admin-select" value={year} onChange={e => setYear(e.target.value)}>
              {(data?.available_years || []).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}

        {period === 'range' && (
          <>
            <div className="ctl-group">
              <label className="ctl-label">From</label>
              <input type="date" className="admin-input ctl-date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="ctl-group">
              <label className="ctl-label">To</label>
              <input type="date" className="admin-input ctl-date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
          </>
        )}

        <div className="ctl-group">
          <label className="ctl-label">Owner</label>
          <select className="admin-select" value={owner} onChange={e => setOwner(e.target.value)}>
            {ownerOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <button
          className="admin-add-btn ctl-add-withdrawal"
          onClick={() => setShowAddWithdrawal(true)}
        >+ Add Withdrawal</button>

        <button
          className="admin-refresh"
          onClick={() => fetchData({ refresh: true })}
          disabled={loading}
          title="Reload from Google Sheets"
        >{loading ? '…' : '↻'}</button>
      </div>

      {showAddWithdrawal && (
        <AddWithdrawalModal
          onClose={() => setShowAddWithdrawal(false)}
          onSaved={() => { setShowAddWithdrawal(false); fetchData({ refresh: true }); }}
        />
      )}

      {loading && !data ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <div className="period-label">
            Showing <strong>{data?.period?.label || 'Lifetime'}</strong>
            {owner !== 'All' && <> · <strong>{owner}</strong></>}
          </div>

          <div className="kpi-grid">
            <Kpi label="Revenue" value={fmtMoney(summary.revenue)} accent="gold" />
            <Kpi label="Cards Sold" value={fmtInt(summary.period_sold)} sub="in period (by sold date)" />
            <Kpi label="Avg Sale" value={fmtMoney(summary.avg_sale)} />
            <Kpi label="Net Profit" value={fmtMoney(summary.net_profit)} sub={summary.refunds ? `${fmtMoney(summary.refunds)} refunded` : null} />
            <Kpi
              label="Active Listings"
              value={fmtInt(summary.active)}
              sub={`of ${fmtInt(summary.listed)} listed · ${summary.sell_through_pct ?? 0}% sold`}
            />
            <Kpi label="Inventory Value" value={fmtMoney(summary.active_value)} sub={`${fmtMoney(summary.potential_sales)} potential`} />
            {summary.awaiting_count > 0 && (
              <Kpi
                label="Awaiting Payout"
                value={fmtMoney(summary.awaiting_payout)}
                sub={`${fmtInt(summary.awaiting_count)} cards sold, not yet paid`}
              />
            )}
            <Kpi label="Withdrawals" value={fmtMoney(summary.withdrawals)} sub="total withdrawn" />
            <Kpi
              label="Total Owed"
              value={fmtMoney(summary.total_owed)}
              sub="paid sales − withdrawals"
              accent="gold"
            />
          </div>

          <Section title="By Owner">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th className="num">Listed</th>
                  <th className="num">Sold</th>
                  <th className="num">Active</th>
                  <th className="num">Revenue</th>
                  <th className="num">Inv. Value</th>
                </tr>
              </thead>
              <tbody>
                {byOwner.length === 0 && (
                  <tr><td colSpan="6" className="admin-empty">No data</td></tr>
                )}
                {byOwner.map(o => (
                  <tr key={o.owner}>
                    <td>{o.owner}</td>
                    <td className="num">{fmtInt(o.listed)}</td>
                    <td className="num">{fmtInt(o.sold)}</td>
                    <td className="num">{fmtInt(o.active)}</td>
                    <td className="num strong">{fmtMoney(o.revenue)}</td>
                    <td className="num muted">{fmtMoney(o.active_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {monthly.length > 0 && (
            <Section title="Monthly Revenue">
              <div className="bars">
                {monthly.map(m => (
                  <div key={m.month} className="bar-row">
                    <div className="bar-label">{fmtMonth(m.month)}</div>
                    <div className="bar-track">
                      <div
                        className="bar-fill"
                        style={{ width: `${Math.max(2, (m.revenue / monthlyMax) * 100)}%` }}
                      />
                    </div>
                    <div className="bar-value">
                      <span className="strong">{fmtMoney(m.revenue)}</span>
                      <span className="muted">  {fmtInt(m.sold)} sold</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {yearly.length > 1 && (
            <Section title="Yearly Totals">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th className="num">Cards Sold</th>
                    <th className="num">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {yearly.map(y => (
                    <tr key={y.year}>
                      <td>{y.year}</td>
                      <td className="num">{fmtInt(y.sold)}</td>
                      <td className="num strong">{fmtMoney(y.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {(topSales.length > 0 || topOrders.length > 0) && (
            <Section title={`Top Sales — ${data?.period?.label || 'Lifetime'}`}>
              <div className="seg seg-inline">
                <button
                  className={`seg-btn ${topMode === 'item' ? 'is-active' : ''}`}
                  onClick={() => setTopMode('item')}
                >By Item</button>
                <button
                  className={`seg-btn ${topMode === 'order' ? 'is-active' : ''}`}
                  onClick={() => setTopMode('order')}
                >By Order</button>
              </div>
              {topMode === 'item' ? (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Card</th>
                      <th>Set</th>
                      <th>Owner</th>
                      <th>Sold</th>
                      <th className="num">Qty</th>
                      <th className="num">Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSales.length === 0 && (
                      <tr><td colSpan="6" className="admin-empty">No sales in period</td></tr>
                    )}
                    {topSales.map((s, i) => (
                      <tr key={`${s.name}-${i}`}>
                        <td>{s.name}</td>
                        <td className="muted">{s.set} {s.number}</td>
                        <td>{s.owner}</td>
                        <td className="muted">{s.sold_date}</td>
                        <td className="num">{s.qty > 1 ? `× ${s.qty}` : '1'}</td>
                        <td className="num strong">{fmtMoney(s.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Owner(s)</th>
                      <th>Sold</th>
                      <th className="num">Cards</th>
                      <th className="num">Total Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topOrders.length === 0 && (
                      <tr><td colSpan="5" className="admin-empty">No orders in period</td></tr>
                    )}
                    {topOrders.map((o) => (
                      <tr key={o.order}>
                        <td className="mono">{o.order}</td>
                        <td>{o.owners}</td>
                        <td className="muted">{o.sold_date}</td>
                        <td className="num">{fmtInt(o.cards)}</td>
                        <td className="num strong">{fmtMoney(o.total_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

// ─── Insights (leaderboards) ──────────────────────────────────────────────────
function InsightsTab() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('lifetime'); // lifetime | year | range
  const [year, setYear] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [owner, setOwner] = useState('All');
  const [moversMode, setMoversMode] = useState('up'); // 'up' (beat value) | 'down' (missed)

  const fetchData = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ period, owner });
      if (period === 'year' && year) params.set('year', year);
      if (period === 'range') {
        if (from) params.set('from', from);
        if (to) params.set('to', to);
      }
      if (opts.refresh) params.set('refresh', '1');
      const res = await authFetch(`/api/admin/insights?${params.toString()}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
      if (!year && json.available_years?.length) {
        setYear(String(json.available_years[json.available_years.length - 1]));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, period, year, from, to, owner]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ownerOptions = useMemo(() => {
    const owners = data?.owners?.length ? data.owners : ['Ben', 'Spencer', 'Spencer\\Ben'];
    return ['All', ...owners];
  }, [data]);

  const fun = data?.fun || {};
  const bestSellers = data?.best_sellers || [];
  const movers = (moversMode === 'up' ? data?.movers_up : data?.movers_down) || [];
  const quickestFlips = data?.quickest_flips || [];
  const aging = data?.aging_inventory || [];
  const topSets = data?.top_sets || [];

  return (
    <div className="analytics">
      {error && <div className="admin-error">{error}</div>}

      <div className="analytics-controls">
        <div className="ctl-group">
          <label className="ctl-label">Period</label>
          <div className="seg">
            {[
              ['lifetime', 'Lifetime'],
              ['year', 'Yearly'],
              ['range', 'Custom'],
            ].map(([v, l]) => (
              <button
                key={v}
                className={`seg-btn ${period === v ? 'is-active' : ''}`}
                onClick={() => setPeriod(v)}
              >{l}</button>
            ))}
          </div>
        </div>

        {period === 'year' && (
          <div className="ctl-group">
            <label className="ctl-label">Year</label>
            <select className="admin-select" value={year} onChange={e => setYear(e.target.value)}>
              {(data?.available_years || []).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        )}

        {period === 'range' && (
          <>
            <div className="ctl-group">
              <label className="ctl-label">From</label>
              <input type="date" className="admin-input ctl-date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="ctl-group">
              <label className="ctl-label">To</label>
              <input type="date" className="admin-input ctl-date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
          </>
        )}

        <div className="ctl-group">
          <label className="ctl-label">Owner</label>
          <select className="admin-select" value={owner} onChange={e => setOwner(e.target.value)}>
            {ownerOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <button
          className="admin-refresh"
          onClick={() => fetchData({ refresh: true })}
          disabled={loading}
          title="Reload from Google Sheets"
        >{loading ? '…' : '↻'}</button>
      </div>

      {loading && !data ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <div className="period-label">
            Showing <strong>{data?.period?.label || 'Lifetime'}</strong>
            {owner !== 'All' && <> · <strong>{owner}</strong></>}
          </div>

          <div className="kpi-grid">
            <Kpi
              label="Best Seller"
              value={fun.best_seller ? fun.best_seller.name : '—'}
              sub={fun.best_seller ? `${fun.best_seller.qty} copies sold` : null}
              accent="gold"
            />
            <Kpi
              label="Avg Days to Sell"
              value={fun.avg_days_to_sell != null ? fmtDays(Math.round(fun.avg_days_to_sell)) : '—'}
              sub={fun.fastest_flip_days != null ? `fastest ${fmtDays(fun.fastest_flip_days)}` : null}
            />
            <Kpi
              label="Most Valuable in Stock"
              value={fun.most_valuable_active ? fmtMoney(fun.most_valuable_active.value) : '—'}
              sub={fun.most_valuable_active ? fun.most_valuable_active.name : null}
            />
            <Kpi
              label="Active Listings Aging"
              value={aging.length ? fmtDays(aging[0].days) : '—'}
              sub={aging.length ? `${aging[0].name} (longest sitting)` : null}
            />
          </div>

          <Section title="Best-Selling Cards — by copies sold">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Card</th>
                  <th>Set</th>
                  <th>Owner</th>
                  <th className="num">Copies</th>
                  <th className="num">Profit</th>
                </tr>
              </thead>
              <tbody>
                {bestSellers.length === 0 && (
                  <tr><td colSpan="5" className="admin-empty">No sales in period</td></tr>
                )}
                {bestSellers.map((s, i) => (
                  <tr key={`${s.name}-${s.set}-${i}`}>
                    <td>{s.name}{s.foil ? <span className="delta-pct">{s.foil}</span> : null}</td>
                    <td className="muted">{s.set} {s.number}</td>
                    <td>{s.owner}</td>
                    <td className="num strong">× {fmtInt(s.qty)}</td>
                    <td className="num">{fmtMoney(s.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Biggest Movers — Est. Value vs Net">
            <div className="seg seg-inline">
              <button
                className={`seg-btn ${moversMode === 'up' ? 'is-active' : ''}`}
                onClick={() => setMoversMode('up')}
              >▲ Beat Value</button>
              <button
                className={`seg-btn ${moversMode === 'down' ? 'is-active' : ''}`}
                onClick={() => setMoversMode('down')}
              >▼ Under Value</button>
            </div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Card</th>
                  <th>Set</th>
                  <th>Sold</th>
                  <th className="num">Est. Value</th>
                  <th className="num">Net</th>
                  <th className="num">Δ</th>
                </tr>
              </thead>
              <tbody>
                {movers.length === 0 && (
                  <tr><td colSpan="6" className="admin-empty">No sales with a valuation in period</td></tr>
                )}
                {movers.map((m, i) => (
                  <tr key={`${m.name}-${m.set}-${i}`}>
                    <td>{m.name}{m.qty > 1 ? <span className="delta-pct">× {m.qty}</span> : null}</td>
                    <td className="muted">{m.set} {m.number}</td>
                    <td className="muted">{m.sold_date}</td>
                    <td className="num muted">{fmtMoney(m.value)}</td>
                    <td className="num strong">{fmtMoney(m.profit)}</td>
                    <td className={`num ${m.delta >= 0 ? 'delta-up' : 'delta-down'}`}>
                      {m.delta >= 0 ? '+' : '−'}{fmtMoney(Math.abs(m.delta))}
                      <span className="delta-pct">{fmtPct(m.pct)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Quickest Flips — listed to sold">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Card</th>
                  <th>Set</th>
                  <th>Listed</th>
                  <th>Sold</th>
                  <th className="num">On Market</th>
                  <th className="num">Profit</th>
                </tr>
              </thead>
              <tbody>
                {quickestFlips.length === 0 && (
                  <tr><td colSpan="6" className="admin-empty">No dated sales in period</td></tr>
                )}
                {quickestFlips.map((f, i) => (
                  <tr key={`${f.name}-${f.set}-${i}`}>
                    <td>{f.name}{f.qty > 1 ? <span className="delta-pct">× {f.qty}</span> : null}</td>
                    <td className="muted">{f.set} {f.number}</td>
                    <td className="muted">{f.listed_date}</td>
                    <td className="muted">{f.sold_date}</td>
                    <td className="num strong">{fmtDays(f.days)}</td>
                    <td className="num">{fmtMoney(f.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Aging Inventory — active listings sitting longest">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Card</th>
                  <th>Set</th>
                  <th>Owner</th>
                  <th>Listed</th>
                  <th className="num">On Market</th>
                  <th className="num">Est. Value</th>
                </tr>
              </thead>
              <tbody>
                {aging.length === 0 && (
                  <tr><td colSpan="6" className="admin-empty">No active listings</td></tr>
                )}
                {aging.map((a, i) => (
                  <tr key={`${a.name}-${a.set}-${i}`}>
                    <td>{a.name}</td>
                    <td className="muted">{a.set} {a.number}</td>
                    <td>{a.owner}</td>
                    <td className="muted">{a.listed_date}</td>
                    <td className="num strong">{fmtDays(a.days)}</td>
                    <td className="num">{fmtMoney(a.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Top Sets — by profit">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Set</th>
                  <th className="num">Cards Sold</th>
                  <th className="num">Profit</th>
                </tr>
              </thead>
              <tbody>
                {topSets.length === 0 && (
                  <tr><td colSpan="3" className="admin-empty">No sales in period</td></tr>
                )}
                {topSets.map((s) => (
                  <tr key={s.set}>
                    <td className="strong">{s.set}</td>
                    <td className="num">{fmtInt(s.sold)}</td>
                    <td className="num strong">{fmtMoney(s.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}

function AddWithdrawalModal({ onClose, onSaved }) {
  const { authFetch } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [withdrawer, setWithdrawer] = useState('Ben');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Enter a positive amount'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await authFetch('/api/admin/withdrawals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, withdrawer, amount: amt }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to add');
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
        <div className="admin-header">
          <div className="admin-title">Add Withdrawal</div>
          <button className="admin-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="admin-error">{error}</div>}
        <div className="withdrawal-form">
          <div className="ctl-group">
            <label className="ctl-label">Date</label>
            <input
              type="date"
              className="admin-input ctl-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="ctl-group">
            <label className="ctl-label">Withdrawer</label>
            <select className="admin-select" value={withdrawer} onChange={(e) => setWithdrawer(e.target.value)}>
              <option value="Ben">Ben</option>
              <option value="Spencer">Spencer</option>
            </select>
          </div>
          <div className="ctl-group">
            <label className="ctl-label">Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="admin-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              autoFocus
            />
          </div>
          <button className="admin-add-btn" onClick={submit} disabled={busy || !amount}>
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`kpi ${accent ? `kpi-${accent}` : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="analytics-section">
      <div className="analytics-section-title">{title}</div>
      {children}
    </div>
  );
}

// ─── Name Audit ──────────────────────────────────────────────────────────────
// Finds rows whose stored name doesn't match the canonical Scryfall name for
// the row's set + collector number, and lets the admin fix them in batches.
const AUDIT_PAGE_SIZE = 20;

function NameAuditTab() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(0);
  const [sevFilter, setSevFilter] = useState('all'); // all | major | minor
  const [unchecked, setUnchecked] = useState(() => new Set()); // row_index that won't be applied
  const [edits, setEdits] = useState({}); // row_index -> overridden target name

  const fetchData = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`/api/admin/name-audit${opts.refresh ? '?refresh=1' : ''}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const items = useMemo(() => data?.items || [], [data]);
  const unresolved = data?.unresolved || [];
  const majorCount = useMemo(() => items.filter(i => i.severity === 'major').length, [items]);
  const minorCount = useMemo(() => items.filter(i => i.severity === 'minor').length, [items]);

  const filtered = useMemo(
    () => (sevFilter === 'all' ? items : items.filter(i => i.severity === sevFilter)),
    [items, sevFilter],
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / AUDIT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = filtered.slice(safePage * AUDIT_PAGE_SIZE, safePage * AUDIT_PAGE_SIZE + AUDIT_PAGE_SIZE);

  const setFilter = (v) => { setSevFilter(v); setPage(0); };
  const toggleCheck = (row) => setUnchecked(prev => {
    const next = new Set(prev);
    if (next.has(row)) next.delete(row); else next.add(row);
    return next;
  });
  const setEdit = (row, val) => setEdits(prev => ({ ...prev, [row]: val }));
  const targetName = (it) => (edits[it.row_index] ?? it.new_name);

  const checkedOnPage = pageItems.filter(it => !unchecked.has(it.row_index));

  const applyChecked = async () => {
    const fixes = checkedOnPage
      .map(it => ({ row_index: it.row_index, name: targetName(it).trim() }))
      .filter(f => f.name);
    if (!fixes.length) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await authFetch('/api/admin/name-audit/fix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fixes }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to apply');
      const fixedRows = new Set(fixes.map(f => f.row_index));
      setData(d => ({ ...d, items: d.items.filter(it => !fixedRows.has(it.row_index)) }));
      setNotice(`Fixed ${json.fixed} name${json.fixed === 1 ? '' : 's'}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <div className="admin-loading">Scanning the sheet against Scryfall…</div>;

  return (
    <div className="analytics">
      {error && <div className="admin-error">{error}</div>}

      <div className="analytics-controls">
        <div className="ctl-group">
          <label className="ctl-label">Severity</label>
          <div className="seg">
            {[
              ['all', `All (${items.length})`],
              ['major', `Major (${majorCount})`],
              ['minor', `Minor (${minorCount})`],
            ].map(([v, l]) => (
              <button
                key={v}
                className={`seg-btn ${sevFilter === v ? 'is-active' : ''}`}
                onClick={() => setFilter(v)}
              >{l}</button>
            ))}
          </div>
        </div>
        {notice && <div className="audit-notice">{notice}</div>}
        <button
          className="admin-refresh"
          onClick={() => fetchData({ refresh: true })}
          disabled={loading || busy}
          title="Re-scan the whole sheet against Scryfall"
        >{loading ? '…' : '↻'}</button>
      </div>

      <div className="audit-summary">
        {items.length === 0
          ? '🎉 Every resolvable row matches its Scryfall name.'
          : <>Showing <strong>{filtered.length === 0 ? 0 : safePage * AUDIT_PAGE_SIZE + 1}–
              {Math.min(filtered.length, (safePage + 1) * AUDIT_PAGE_SIZE)}</strong> of{' '}
              <strong>{filtered.length}</strong> mismatch{filtered.length === 1 ? '' : 'es'}</>}
      </div>

      {pageItems.map(it => (
        <div key={it.row_index} className={`audit-row ${unchecked.has(it.row_index) ? 'is-skipped' : ''}`}>
          <a href={it.scryfall_url} target="_blank" rel="noopener noreferrer" className="audit-thumb-link" title="Open on Scryfall">
            {it.image_small
              ? <img className="audit-thumb" src={it.image_small} alt={it.new_name} loading="lazy" />
              : <div className="audit-thumb audit-thumb-empty">no img</div>}
          </a>
          <div className="audit-body">
            <div className="audit-line">
              <span className="audit-old">{it.old_name}</span>
              <span className="audit-arrow">→</span>
              <input
                className="admin-input audit-new-input"
                value={targetName(it)}
                onChange={(e) => setEdit(it.row_index, e.target.value)}
                spellCheck={false}
              />
            </div>
            <div className="audit-meta">
              <span className={`audit-sev audit-sev-${it.severity}`}>{it.severity}</span>
              <span className="audit-muted">{(it.set || '').toUpperCase()} · #{it.number} · row {it.row_index}</span>
              <a href={it.scryfall_url} target="_blank" rel="noopener noreferrer" className="audit-link">View on Scryfall ↗</a>
            </div>
          </div>
          <label className="audit-check" title="Apply this fix">
            <input
              type="checkbox"
              checked={!unchecked.has(it.row_index)}
              onChange={() => toggleCheck(it.row_index)}
            />
          </label>
        </div>
      ))}

      {filtered.length > 0 && (
        <div className="audit-footer">
          <div className="audit-pager">
            <button
              className="seg-btn"
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage <= 0 || busy}
            >← Prev</button>
            <span className="audit-muted">Page {safePage + 1} / {pageCount}</span>
            <button
              className="seg-btn"
              onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
              disabled={safePage >= pageCount - 1 || busy}
            >Next →</button>
          </div>
          <button
            className="admin-add-btn"
            onClick={applyChecked}
            disabled={busy || checkedOnPage.length === 0}
          >{busy ? 'Applying…' : `Apply ${checkedOnPage.length} checked`}</button>
        </div>
      )}

      {unresolved.length > 0 && (
        <details className="audit-unresolved">
          <summary>{unresolved.length} unresolved row{unresolved.length === 1 ? '' : 's'} (bad set / collector number — fix in the sheet)</summary>
          <table className="admin-table">
            <thead>
              <tr><th>Name</th><th>Set</th><th>#</th><th>Row</th><th>Why</th><th></th></tr>
            </thead>
            <tbody>
              {unresolved.map(u => (
                <tr key={u.row_index}>
                  <td>{u.name}</td>
                  <td className="audit-muted">{u.set}</td>
                  <td className="audit-muted">{u.number}</td>
                  <td className="audit-muted">{u.row_index}</td>
                  <td className="audit-muted">{u.reason}</td>
                  <td>
                    <a
                      href={`https://scryfall.com/search?q=${encodeURIComponent(u.name)}`}
                      target="_blank" rel="noopener noreferrer" className="audit-link"
                    >Search ↗</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

// ─── Whitelist (unchanged behaviour, just split into a tab) ──────────────────
function WhitelistTab() {
  const { authFetch } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/whitelist');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setEntries(data.entries || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const addEntry = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setBusy(true);
    setError('');
    try {
      const res = await authFetch('/api/whitelist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role: newRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add');
      setEntries(data.entries);
      setNewEmail('');
      setNewRole('user');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (email) => {
    if (!window.confirm(`Remove ${email} from the whitelist?`)) return;
    setBusy(true);
    setError('');
    try {
      const res = await authFetch(`/api/whitelist/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove');
      setEntries(data.entries);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <div className="admin-error">{error}</div>}

      <div className="admin-add">
        <input
          type="email"
          className="admin-input"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="email@example.com"
          onKeyDown={(e) => e.key === 'Enter' && addEntry()}
          disabled={busy}
        />
        <select
          className="admin-select"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          disabled={busy}
        >
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <button className="admin-add-btn" onClick={addEntry} disabled={busy || !newEmail}>
          + Add
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.email}>
                  <td>{e.email}</td>
                  <td>
                    <span className={`admin-role-pill admin-role-${e.role}`}>
                      {e.role}
                    </span>
                  </td>
                  <td>
                    <button
                      className="admin-remove"
                      onClick={() => removeEntry(e.email)}
                      disabled={busy}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan="3" className="admin-empty">No entries</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
